import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Check, Users, PlusCircle } from 'lucide-react';
import { Meeting, MeetingType } from './types';
import { meetingsService, SystemUserOption } from './meetingsService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Meeting>, selectedParticipants?: SystemUserOption[]) => Promise<void>;
}

const getSessionUser = () => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_user') : null;
    if (raw) {
      const u = JSON.parse(raw);
      return {
        id: u.id || 'USR-CURRENT',
        name: u.display_name || u.username || 'Current User',
        role: u.user_type || u.role || 'User',
        department: u.department || 'Safety & Flight Operations',
      };
    }
  } catch {}
  return { id: 'USR-FO-01', name: 'Captain Burak Demir', role: 'User', department: 'Safety & Flight Operations' };
};

const getLocalDateTimeString = (offsetMs: number = 0) => {
  const d = new Date(Date.now() + offsetMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const MeetingCreateModal: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [meetingTypeId, setMeetingTypeId] = useState('MT-SRB');
  const [isCustomType, setIsCustomType] = useState(false);
  const [customTypeName, setCustomTypeName] = useState('');
  const [description, setDescription] = useState('');
  const [plannedStartTime, setPlannedStartTime] = useState(() => getLocalDateTimeString(0));
  const [plannedEndTime, setPlannedEndTime] = useState(() => getLocalDateTimeString(7200000));
  const [location, setLocation] = useState('HQ Main Executive Conference Room');
  const [chairName, setChairName] = useState('Cemil Demir (Accountable Manager)');
  const [organizerName, setOrganizerName] = useState(() => getSessionUser().name);
  const [isQuorumRequired, setIsQuorumRequired] = useState(true);
  const [isMgmtReview, setIsMgmtReview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Participants State
  const [allUsers, setAllUsers] = useState<SystemUserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SystemUserOption[]>([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      const u = getSessionUser();
      setOrganizerName(u.name);
      setPlannedStartTime(getLocalDateTimeString(0));
      setPlannedEndTime(getLocalDateTimeString(7200000));
      setError(null);
      setIsCustomType(false);
      setCustomTypeName('');
      setSelectedUsers([]);
      setUserSearch('');

      // Load types & registered users
      meetingsService.getMeetingTypes().then((types) => {
        if (types && types.length > 0) {
          setMeetingTypes(types);
          setMeetingTypeId(types[0].id);
        }
      });

      meetingsService.getSystemUsers().then((users) => {
        if (users && users.length > 0) {
          setAllUsers(users);
        }
      });
    }
  }, [isOpen]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return allUsers;
    const s = userSearch.toLowerCase();
    return allUsers.filter(
      (u) =>
        u.display_name.toLowerCase().includes(s) ||
        u.email.toLowerCase().includes(s) ||
        u.role.toLowerCase().includes(s) ||
        (u.department && u.department.toLowerCase().includes(s))
    );
  }, [allUsers, userSearch]);

  const toggleUserSelection = (user: SystemUserOption) => {
    if (selectedUsers.some((u) => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const removeSelectedUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u.id !== userId));
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Meeting title is required.');
      return;
    }

    if (isCustomType && !customTypeName.trim()) {
      setError('Please enter a custom meeting type name.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const u = getSessionUser();

      const finalTypeId = isCustomType ? `MT-CUSTOM-${Date.now()}` : meetingTypeId;
      const finalTitle = title.trim();

      // If user entered custom meeting type, optionally register it in meeting types
      if (isCustomType && customTypeName.trim()) {
        meetingsService.createMeetingType({
          id: finalTypeId,
          type_code: customTypeName.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0, 16),
          title: customTypeName.trim(),
          category: 'GOVERNANCE',
          requires_quorum: isQuorumRequired,
          is_management_review: isMgmtReview
        }).catch(() => {});
      }

      await onSubmit({
        title: finalTitle,
        meeting_type_id: isCustomType ? customTypeName.trim() : finalTypeId,
        description,
        target_department: 'Safety & Flight Operations', // default internal value preserved for backward compatibility
        planned_start_time: plannedStartTime.replace('T', ' ') + ':00',
        planned_end_time: plannedEndTime.replace('T', ' ') + ':00',
        location,
        chair_name: chairName,
        chair_id: 'USR-AM-01',
        organizer_name: organizerName || u.name,
        organizer_id: u.id || 'USR-FO-01',
        quorum_required: isQuorumRequired,
        is_management_review: isMgmtReview
      }, selectedUsers);

      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-2xl" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Requirement 1: Only "Schedule Meeting" title, NO subtitle */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>Schedule Meeting</h3>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            {/* Meeting Title */}
            <div className="form-field">
              <label>Meeting Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Q2 2026 Safety Review Board (SRB)"
                required
              />
            </div>

            {/* Requirement 2: Meeting Type with Manual Custom Entry Option */}
            <div className="form-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ margin: 0 }}>Meeting Type *</label>
                <button
                  type="button"
                  onClick={() => setIsCustomType(!isCustomType)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: 0
                  }}
                >
                  <PlusCircle size={13} />
                  {isCustomType ? 'Choose from Existing Types' : '+ Enter Custom Type (Manuel Giriş)'}
                </button>
              </div>

              {!isCustomType ? (
                <select
                  value={meetingTypeId}
                  onChange={(e) => {
                    if (e.target.value === '__CUSTOM__') {
                      setIsCustomType(true);
                    } else {
                      setMeetingTypeId(e.target.value);
                      const selected = meetingTypes.find(t => t.id === e.target.value);
                      if (selected?.is_management_review) setIsMgmtReview(true);
                      if (selected?.requires_quorum) setIsQuorumRequired(true);
                    }
                  }}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                >
                  {meetingTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title} ({t.type_code})
                    </option>
                  ))}
                  <option value="__CUSTOM__">+ Other / Enter Custom Type (Özel Tür Girişi)...</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={customTypeName}
                    onChange={(e) => setCustomTypeName(e.target.value)}
                    placeholder="Enter custom meeting type (e.g. Fatigue Safety Action Group - FSAG)"
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #3b82f6', fontSize: '13px' }}
                    autoFocus
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setIsCustomType(false)}
                    style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Select List
                  </button>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="form-field">
              <label>Description & Objectives</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Primary topics, regulatory context, or strategic safety objectives..."
              />
            </div>

            {/* Date & Time */}
            <div className="form-grid-2">
              <div className="form-field">
                <label>Planned Start Time *</label>
                <input
                  type="datetime-local"
                  value={plannedStartTime}
                  onChange={(e) => setPlannedStartTime(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Planned End Time *</label>
                <input
                  type="datetime-local"
                  value={plannedEndTime}
                  onChange={(e) => setPlannedEndTime(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div className="form-field">
              <label>Location / Virtual Room</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            {/* Chair & Organizer */}
            <div className="form-grid-2">
              <div className="form-field">
                <label>Meeting Chair</label>
                <input
                  type="text"
                  value={chairName}
                  onChange={(e) => setChairName(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Organizer / Secretary</label>
                <input
                  type="text"
                  value={organizerName}
                  onChange={(e) => setOrganizerName(e.target.value)}
                />
              </div>
            </div>

            {/* Requirement 5: Internal Participants / Şirket İçi Katılımcılar */}
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '14px',
              marginTop: '8px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>
                  <Users size={16} color="#2563eb" />
                  <span>Internal Participants (Şirket İçi Katılımcılar)</span>
                </div>
                <span style={{ fontSize: '11px', background: selectedUsers.length > 0 ? '#dbeafe' : '#e2e8f0', color: selectedUsers.length > 0 ? '#1e40af' : '#64748b', padding: '2px 8px', borderRadius: '12px', fontWeight: 500 }}>
                  {selectedUsers.length} selected
                </span>
              </div>

              {/* Selected Pills */}
              {selectedUsers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {selectedUsers.map((u) => (
                    <span
                      key={u.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#ffffff',
                        border: '1px solid #93c5fd',
                        color: '#1e3a8a',
                        padding: '3px 8px',
                        borderRadius: '16px',
                        fontSize: '11px',
                        fontWeight: 500
                      }}
                    >
                      <span>{u.display_name}</span>
                      <button
                        type="button"
                        onClick={() => removeSelectedUser(u.id)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#64748b' }}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* User Search Bar */}
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '10px', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Search registered personnel by name, email, or role..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '7px 10px 7px 32px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '12px',
                    boxSizing: 'border-box',
                    background: '#ffffff'
                  }}
                />
              </div>

              {/* Scrollable Registered Users List */}
              <div style={{
                maxHeight: '140px',
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                background: '#ffffff'
              }}>
                {filteredUsers.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>
                    No matching personnel found
                  </div>
                ) : (
                  filteredUsers.map((u) => {
                    const isSelected = selectedUsers.some((sel) => sel.id === u.id);
                    return (
                      <div
                        key={u.id}
                        onClick={() => toggleUserSelection(u)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: 'pointer',
                          background: isSelected ? '#eff6ff' : 'transparent',
                          transition: 'background 0.15s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            border: isSelected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                            background: isSelected ? '#2563eb' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff'
                          }}>
                            {isSelected && <Check size={12} strokeWidth={3} />}
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>
                              {u.display_name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              {u.email}
                            </div>
                          </div>
                        </div>
                        <span style={{
                          fontSize: '10px',
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {u.role ? u.role.replace(/_/g, ' ') : 'MEMBER'}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Governance Checkboxes */}
            <div className="flex gap-6 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs">
              <label className="flex items-center gap-2 cursor-pointer text-gray-700 font-medium">
                <input
                  type="checkbox"
                  checked={isQuorumRequired}
                  onChange={(e) => setIsQuorumRequired(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Enforce Quorum Validation
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-gray-700 font-medium">
                <input
                  type="checkbox"
                  checked={isMgmtReview}
                  onChange={(e) => setIsMgmtReview(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Official Management Review
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Scheduling...' : 'Schedule Meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
